import cn from 'clsx';
import { motion, useIsPresent, Variants } from 'framer-motion';
import { FC, HTMLAttributes, useCallback, useEffect, useMemo } from 'react';

import Icon from '../Icon';

import s from './Toast.module.scss';
import { ToastOptions } from './Toast.types';
import ToastActionButton from './ToastActionButton';
import { useToastContext } from './ToastContext';

type NativeAttrs = Omit<HTMLAttributes<HTMLElement>, keyof ToastOptions>;

export type ToastWrapperProps = ToastOptions & NativeAttrs;

const toastMotionVariants: Variants = {
  initial: {
    opacity: 0,
    y: 24
  },
  animate: {
    opacity: 1,
    y: -4,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.85,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 1, 1]
    }
  }
};

const ToastWrapper: FC<React.PropsWithChildren<React.PropsWithChildren<ToastWrapperProps>>> = ({
  id,
  icon,
  duration = 3000,
  iconColor,
  btnName,
  open = false,
  content,
  animated,
  onClose,
  onClickButton,
  className,
  containerClassName,
  ...props
}) => {
  const { close } = useToastContext();
  const isPresent = useIsPresent();

  const handleClose = useCallback(() => {
    close(id);
    if (onClose) {
      onClose();
    }
  }, [close, id, onClose]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isPresent) {
        handleClose();
      }
    }, duration);

    return () => {
      clearTimeout(timer);
    };
  }, [duration, handleClose, isPresent]);

  const renderChildren = useMemo(
    () => (
      <div className={cn('page-padding m-auto w-full max-w-[520px]', containerClassName)}>
        <div tabIndex={-1} className={cn(s.toast, className)} {...props}>
          {icon && <Icon className={cn(s.toast__icon, icon === 'state-progress' && s[`toast__icon__${icon}`])} type={icon} color={iconColor} size={24} />}
          <span>{content}</span>
          {btnName && <ToastActionButton className={s.toast__button} btnName={btnName} onClose={handleClose} onClick={onClickButton} />}
        </div>
      </div>
    ),
    [content, icon, iconColor, handleClose, btnName, onClickButton, props, className, containerClassName]
  );

  const conditionalRender = useMemo(() => {
    if (animated) {
      return (
        <motion.div layout variants={toastMotionVariants} initial="initial" animate="animate" exit="exit">
          {renderChildren}
        </motion.div>
      );
    }
    return open ? renderChildren : null;
  }, [animated, open, renderChildren]);

  return conditionalRender;
};

export default ToastWrapper;
